import { emptyList, ItemList } from '../../../common/models/ItemList'
import { Observable, of } from 'rxjs'
import { Container, IInit } from '../../../common/container/Container'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { Query } from '../../../common/api/Query'
import { Genes, GenesQuery } from '../models/Genes'
import { GenesContainerConfig } from '../container'
import { GenesDTO, toModel } from '../models/GenesDTO'
import { prepareURL } from '../../../common/api/http-helpers'
import { catchError, map } from 'rxjs/operators'

export interface IGenesApi extends IInit {
  getByID(id: string): Observable<Genes | undefined>

  getFilteredList(q: Query<GenesQuery>): Observable<ItemList<Genes>>

  add(e: GenesDTO): Observable<Genes | undefined>

  update(e: GenesDTO): Observable<Genes | undefined>

  delete(id: string): Observable<boolean>
}

export class GenesApi implements IGenesApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as GenesContainerConfig).moduleFullUrl
  }

  add(e: GenesDTO): Observable<Genes | undefined> {
    return this._httpClient.post<Genes>({ url: this._url, body: e }).pipe(
      map<GenesDTO, Genes>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: this._url + '/' + id }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<Genes | undefined> {
    return this._httpClient.get<Genes>({ url: `${this._url}/${id}` }).pipe(
      map<GenesDTO, Genes>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  getFilteredList(q: Query<GenesQuery>): Observable<ItemList<Genes>> {
    return this._httpClient.get<ItemList<Genes>>({ url: prepareURL(this._url, q) }).pipe(
      map<ItemList<GenesDTO>, ItemList<Genes>>((dto) => {
        const itemList = emptyList<Genes>()
        itemList.count = dto.count
        itemList.items = dto.items.map((d) => toModel(d))
        return itemList
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(emptyList<Genes>())
      })
    )
  }

  update(e: GenesDTO): Observable<Genes | undefined> {
    return this._httpClient.put<Genes>({ url: this._url, body: e }).pipe(
      map<GenesDTO, Genes>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }
}
